import { computed, inject, ref, watch } from "vue";
import { SUBFORM_PORT } from "@/shared/contracts/ports";
import type { EntityDefinition, RelationDefinition } from "@/shared/contracts/structure";
import type SubformWidget from "./widgets/SubformWidget.vue";

/** Relationship titles describe the child collection, not its pointer to the parent. */
export function useChildForms(relations: () => readonly RelationDefinition[]) {
	const port = inject(SUBFORM_PORT, null);
	const definitions = ref<Record<string, EntityDefinition>>({});
	const childForms = ref<InstanceType<typeof SubformWidget>[]>([]);
	watch(
		relations,
		async (links, _, onCleanup) => {
			let current = true;
			onCleanup(() => {
				current = false;
			});
			const entities = await Promise.all(
				[...new Set(links.map((r) => r.toEntityCode))].map((code) =>
					port?.entityOf(code).catch(() => null),
				),
			);
			if (current)
				definitions.value = Object.fromEntries(
					entities.filter((e) => e != null).map((e) => [e.code, e]),
				);
		},
		{ immediate: true },
	);
	function label(relation: RelationDefinition) {
		const name = definitions.value[relation.toEntityCode]?.name;
		if (!relation.inverse || !name) return relation.label;
		return relations().filter((r) => r.toEntityCode === relation.toEntityCode).length > 1
			? `${name} · ${relation.label}`
			: name;
	}
	return {
		openChild: (entity: string, id: string) => port?.open?.(entity, id),
		childForms,
		definitions,
		label,
		childDirty: computed(() => childForms.value.some((form) => form.dirty)),
	};
}
